import React from "react";
import { AxiosError } from "axios";
import { QueryResult, QueryKey } from "react-query";
import { useTranslation } from "react-i18next";

import { ApiError } from "fe-shared/src/types/common";

import { ReturnIcon } from "fe-shared/src/components/Icons";
import {
  LinkButton,
  Loader,
  CollapseComponent,
  NotFoundPage,
  RecordsSidebar,
} from "fe-shared/src/components";

import TransferStatus from "../TransferStatus";
import { TRANSFERS_ACTIVITIES_QUERY_KEY } from "../../consts";
import { Transfer } from "../../types";
import InvoiceLink from "../InvoiceLink";
import TransferContentView from "../TransferContentView";
import TransferActions from "./containers/TransferActions";
import { DialogHeader } from "..";

import * as api from "./modules/api";

import styles from "./TransferDetails.module.scss";

interface Props {
  returnUrl: string;
  transfer: QueryResult<Transfer, AxiosError<ApiError>>;
  transferQueryKey: QueryKey;
  isStaffView?: boolean;
  hasSidebar?: boolean;
  canExportHistory?: boolean;
  canViewNotes?: boolean;
  canAddNote?: boolean;
  canExportNotes?: boolean;
  canEditTransfer?: boolean;
  canCancelTransfer?: boolean;
  canInvalidateTransfer?: boolean;
  userEmail?: string;
}

const TransferDetailsView = (props: Props) => {
  const {
    returnUrl,
    transfer,
    isStaffView = false,
    hasSidebar,
    canExportHistory,
    canViewNotes,
    canAddNote,
    canExportNotes,
    userEmail,
    transferQueryKey,
    canEditTransfer,
    canCancelTransfer,
    canInvalidateTransfer,
  } = props;
  const { data, isLoading, isError } = transfer;

  const { t } = useTranslation();

  const isTranferNotFound = transfer.error?.response?.status === 404;

  const renderContent = () => {
    const { transferType, transferId, transferNumber } = data!;

    const activitiesQueryKey: QueryKey = [
      TRANSFERS_ACTIVITIES_QUERY_KEY,
      transferNumber,
    ];

    const getDialogTitle = (title: string) => (
      <DialogHeader
        type={transferType}
        title={title}
        number={transferNumber}
        translationRoot="transfers"
      />
    );

    return (
      <>
        <main className={styles.transferDetailsContent}>
          <div className={styles.transferDetailsContentWrapper}>
            <div className={styles.transferDetailsNavigation}>
              <LinkButton
                to={returnUrl}
                variant="inline"
                id="transfer-back-link"
                iconComponent={ReturnIcon}
              >
                {t("transfers.grid.title")}
              </LinkButton>
            </div>
            <div className={styles.transferDetailsData}>
              <TransferStatus transfer={data!} />
              <InvoiceLink
                prefix={t("invoices.linkPrefix")}
                invoiceNumber={data?.invoiceNumber}
                className={styles.transferDetailsInvoice}
              />
              <TransferContentView
                transfer={data!}
                isStaffView={isStaffView}
                canEditTransfer={canEditTransfer}
              />
              <TransferActions
                isStaffView={isStaffView}
                canCancelTransfer={canCancelTransfer}
                canInvalidateTransfer={canInvalidateTransfer}
                transferQueryKey={transferQueryKey}
                activitiesQueryKey={activitiesQueryKey}
                transfer={data!}
              />
            </div>
          </div>
        </main>
        {hasSidebar && (
          <CollapseComponent storageKey="sidebarCollapsed">
            <RecordsSidebar
              objectId={transferNumber}
              entityType="transfers"
              entityName={t("transfers.details.entityName")}
              initiatorEmail={userEmail || ""}
              getDialogTitle={getDialogTitle}
              fileNamePrefix={t("transfers.details.export.fileNamePrefix", {
                transferNumber,
              })}
              fileNameSuffix=""
              canViewHistory
              canExportHistory={canExportHistory}
              activitiesQueryKey={activitiesQueryKey}
              canViewNotes={canViewNotes}
              canAddNote={canAddNote}
              canExportNotes={canExportNotes}
              notesQueryKey={["transfersNotes", transferNumber]}
              getNotes={() => api.getTransferNotes(transferId)}
              addNote={(text) => {
                return api.addTransferNote({ transferId, text });
              }}
            />
          </CollapseComponent>
        )}
      </>
    );
  };

  return (
    <div className={styles.transferDetails}>
      {isLoading && <Loader isBlocking />}
      {!isLoading && !isError && data && renderContent()}
      {isError && (
        <NotFoundPage
          returnLink="/transfers"
          title={
            isTranferNotFound
              ? "transfers.details.error.notFoundTitle"
              : "errors.somethingWrong"
          }
          descriptions={["transfers.details.error.description"]}
          returnLinkDescription="transfers.details.error.returnLink"
        />
      )}
    </div>
  );
};

export default TransferDetailsView;
